import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { otpInitiate, otpVerify, otpResend, setSocietyAdminData } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { isValidIndianMobile, normalizeIndianMobile } from '../utils/phoneUtils';
import './SocietyAdminLogin.css';

const COMMITTEE_ROLES = [
  'admin',
  'moderator',
  'secretary',
  'treasurer',
  'committee',
  'president',
  'chairman',
  'body_member',
  'society_admin',
  'society_staff',
];

const SocietyAdminLogin: React.FC = () => {
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const cleaned = normalizeIndianMobile(phone);
    if (!isValidIndianMobile(phone)) {
      setError('Please enter a valid 10-digit Indian mobile number');
      return;
    }
    setLoading(true);
    try {
      const res = await otpInitiate(cleaned);
      if (res.data.userExists) {
        setStep('otp');
        setResendTimer(30);
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      } else {
        setError('No account found with this number. Please register on the TappyTray mobile app first.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...otp];
    next[index] = value.slice(-1);
    setOtp(next);
    if (value && index < 3) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const otpStr = otp.join('');
    if (otpStr.length !== 4) { setError('Please enter the 4-digit OTP'); return; }
    const cleaned = normalizeIndianMobile(phone);
    setLoading(true);
    try {
      const res = await otpVerify(cleaned, otpStr);
      const user = res.data.user;

      const committeeSOcieties = (user.societies || []).filter(s =>
        s.roles?.some(r => COMMITTEE_ROLES.includes(r.toLowerCase()))
      );

      if (committeeSOcieties.length === 0) {
        setError('You do not have committee/admin access for any society. Contact your society admin.');
        return;
      }

      const primary = committeeSOcieties[0];
      setSocietyAdminData({
        societyId: primary.society_id,
        societyName: primary.society_name,
        userId: user.id,
        userName: user.name,
        societies: committeeSOcieties,
      });

      authLogin(res.data.token, 'society_admin', user.name);
      navigate('/society/overview');
    } catch (err: any) {
      setError(err.message || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    const cleaned = normalizeIndianMobile(phone);
    try {
      await otpResend(cleaned);
      setResendTimer(30);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to resend OTP');
    }
  };

  return (
    <div className="society-login-container">
      <div className="society-login-card">
        <div className="society-login-header">
          <div className="society-login-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2m-16 0H3m6-10h.01M13 11h.01M9 15h.01M13 15h.01M9 7h.01M13 7h.01" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1>Society Admin Portal</h1>
          <p>Billing Reconciliation for Society Committee</p>
        </div>

        {step === 'phone' ? (
          <form onSubmit={handlePhoneSubmit} className="society-login-form">
            {error && <div className="error-message">{error}</div>}
            <div className="form-group">
              <label htmlFor="phone">Mobile Number</label>
              <div className="phone-input-wrapper">
                <span className="country-code">+91</span>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ''))}
                  maxLength={10}
                  placeholder="Enter your mobile number"
                  autoFocus
                />
              </div>
            </div>
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleOtpSubmit} className="society-login-form">
            {error && <div className="error-message">{error}</div>}
            <p className="otp-sent-text">
              OTP sent to <strong>+91 {normalizeIndianMobile(phone)}</strong>
              <button type="button" className="change-number" onClick={() => { setStep('phone'); setOtp(['','','','']); setError(''); }}>
                Change
              </button>
            </p>
            <div className="otp-inputs">
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => { otpRefs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  className="otp-input"
                  autoComplete="one-time-code"
                />
              ))}
            </div>
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>
            <button
              type="button"
              className="resend-button"
              onClick={handleResend}
              disabled={resendTimer > 0}
            >
              {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default SocietyAdminLogin;
